import { TextareaHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helper?: string;
  showCount?: boolean;
  maxLength?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helper,
      showCount = false,
      maxLength,
      className,
      disabled,
      value,
      ...props
    },
    ref
  ) => {
    const textareaClasses = clsx(
      'block w-full rounded-lg border transition-colors',
      'focus:outline-none focus:ring-2 focus:ring-offset-0',
      'px-4 py-2',
      error
        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
        : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500',
      disabled && 'bg-gray-100 cursor-not-allowed',
      'resize-y',
      className
    );

    const currentLength =
      typeof value === 'string' ? value.length : String(value || '').length;

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          className={textareaClasses}
          {...props}
        />
        <div className="mt-1 flex items-center justify-between">
          <div className="flex-1">
            {error && <p className="text-sm text-red-600">{error}</p>}
            {helper && !error && <p className="text-sm text-gray-500">{helper}</p>}
          </div>
          {showCount && maxLength && (
            <p className="text-sm text-gray-500 ml-2">
              {currentLength} / {maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
